<template>
  <div class="project-notes">
    <!-- Header -->
    <div class="notes-header">
      <h2 class="component-title"></h2>
      <div class="header-actions">
        <button 
          class="btn btn-primary"
          @click="openCreateNoteModal"
          :disabled="loading"
        >
          <span class="btn-icon">+</span>
          {{ $t('notes.addNote') }}
        </button>
      </div>
    </div>    

    <!-- Loading State -->
    <div v-if="loading && notes.length === 0" class="loading-indicator">
      <div class="spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchNotes" class="btn btn-secondary">{{ $t('common.retry') }}</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="notes.length === 0 && !loading" class="empty-state">
      <svg viewBox="0 0 24 24" width="60" height="60">
        <path fill="var(--neutral-300)" d="M19,3H4.99C3.89,3 3,3.9 3,5L3,19C3,20.1 3.89,21 4.99,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M19,19H4.99V5H19V19M5,16H19V19H5V16M5,7H19V10H5V7Z" />
      </svg>
      <p>{{ $t('notes.noNotesAvailable') }}</p>
      <button class="btn btn-primary" @click="openCreateNoteModal">
        {{ $t('notes.addFirstNote') }}
      </button>
    </div>

    <!-- Notes Grid -->
    <div v-else class="grid-content">
      <AppDataGrid
        :columnDefs="columnDefs"
        :rowData="notes"
        :defaultColDef="defaultColDef"
        :pageSizeOptions="[10, 20, 50]"
        :pagination="true"
        :pageSize="20"
        :suppressPaginationPanel="false"
        :animateRows="true"
        :showSearch="true"
        :searchPlaceholder="$t('notes.searchPlaceholder')"
        @grid-ready="onGridReady"
        @action-clicked="handleAction"
        :actions="actions"
        gridStyle="width: 100%;"
        domLayout="autoHeight"
      />
    </div>    <!-- Note Modal -->
    <NoteModal
      v-if="showNoteModal"
      :note="editingNote"
      :project-id="projectId"
      @save="handleNoteSave"
      @close="closeNoteModal"
    />

    <!-- Discussion Modal -->
    <NoteDiscussionModal
      v-if="showDiscussionModal"
      :note="discussingNote"
      :project-id="projectId"
      @close="closeDiscussionModal"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :isVisible="showDeleteModal"
      :title="$t('notes.deleteConfirm.title')"
      :message="$t('notes.deleteConfirm.message', { title: noteToDelete?.title })"
      @confirm="handleDeleteConfirm"
      @cancel="closeDeleteModal"
    />
  </div>

</template>

<script setup lang="ts">
// Follow repo pattern to avoid noisy Vue import typing in some environments
// @ts-ignore
import { ref, onMounted, watch, computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import notesService from '@/services/notes.service';
import AppDataGrid from '@/components/base/AppDataGrid.vue';
import NoteModal from './NoteModal.vue';
import { useRouter } from 'vue-router'
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import { useStore } from '@/composables/useStore';

interface Note {
  id?: string | number;
  title?: string;
  content?: string;
  createdAt?: string;
  createdBy?: any;
  createdByName?: string;
  priority?: string;
  [key: string]: any;
}

const props = defineProps<{ projectId: string | number }>();
const emit = defineEmits<{ (e: 'return-to-project'): void }>();

const { t } = useI18n();
const store = useStore();

// Reactive state
const notes = ref<Note[]>([]);
const loading = ref(false);
const error = ref('');
// Modals
const showNoteModal = ref(false);
const showDeleteModal = ref(false);
const editingNote = ref<Note | null>(null);
const noteToDelete = ref<Note | null>(null);

const router = useRouter()

// AG Grid configuration - make reactive to language changes
const columnDefs = computed(() => [
  {
    field: 'title',
    headerName: t('notes.title'),
    type: 'text',
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdAt',
    headerName: t('notes.creationDate'),
    type: 'timestamp',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdByName',
    headerName: t('notes.createdBy'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      return params.value || t('notes.unknownUser');
    }
  },
  {
    field: 'priority',
    headerName: t('notes.priority.label'),
    type: 'text',
    width: 120,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const priority = params.value as string | undefined;
      if (!priority) return '';

      const priorityClass = `priority-${priority.toLowerCase()}`;
      const priorityText = t(`notes.priority.${priority.toLowerCase()}`);

      return `<span class="priority-badge ${priorityClass}">${priorityText}</span>`;
    }
  }
]);

// Actions configuration for AppDataGrid - make reactive to language changes
const actions = computed(() => [
  {
    name: 'discuss',
    icon: 'fas fa-comments',
    label: t('notes.discuss'),
    tooltip: t('notes.discuss'),
    class: 'discuss-btn'
  },
  {
    name: 'edit',
    icon: 'fas fa-edit',
    label: t('common.edit'),
    tooltip: t('common.edit'),
    class: 'edit-btn',
    condition: (data: any) => {
      const user = store.getters.getUser.value as any;
      try {
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  },
  {
    name: 'delete',
    icon: 'fas fa-trash',
    label: t('common.delete'),
    tooltip: t('common.delete'),
    class: 'delete-btn',
    condition: (data: any) => {
      const user = store.getters.getUser.value as any;
      try {
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  }
]);

const defaultColDef = ref({
  sortable: true,
  resizable: true,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1.4'
  }
});

// Fetch notes
const fetchNotes = async () => {
  try {
    loading.value = true;
    error.value = '';

  const response = await notesService.getAllProjectNotes(props.projectId);
  // Some service calls return a wrapper object (e.g. { content: [...] }) — be tolerant
  notes.value = ((response as unknown) as Note[]) || [];

  } catch (err: any) {
    console.error('Error fetching notes:', err);
    error.value = err.message || t('notes.errorFetching');
  } finally {
    loading.value = false;
  }
};

// Note operations
const openCreateNoteModal = () => {
  editingNote.value = null;
  showNoteModal.value = true;
};

const editNote = (note: Note | any) => {
  if (!note) {
    console.error('ProjectNotes: Cannot edit note - note is undefined or null');
    return;
  }

  editingNote.value = { ...note } as Note;
  showNoteModal.value = true;
};

const closeNoteModal = () => {
  showNoteModal.value = false;
  editingNote.value = null;
};

const handleNoteSave = async (noteData: any) => {
  try {
    if (editingNote.value) {
      // Update existing note
      await notesService.updateNote(props.projectId, String(editingNote.value.id), noteData);
    } else {
      // Create new note
      await notesService.createNote(props.projectId, noteData);
    }
    closeNoteModal();

    fetchNotes();

  } catch (err) {
    console.error('Error saving note:', err);
    // Modal will handle error display
    throw err;
  }
};

const deleteNote = (note: Note | any) => {
  noteToDelete.value = note;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  noteToDelete.value = null;
};

const openDiscussionModal = (note: Note | any) => {
  // Navigate to the discussion page for the note instead of opening a modal
  if (!note || !note.id) return
  router.push({ name: 'NoteDiscussion', params: { projectId: String(props.projectId), noteId: String(note.id) } })
}

const handleDeleteConfirm = async () => {
  try {
    await notesService.deleteNote(props.projectId, String(noteToDelete.value?.id));
    closeDeleteModal();

    fetchNotes();

  } catch (err: any) {
    console.error('Error deleting note:', err);
    error.value = err.message || t('notes.errorDeleting');
    closeDeleteModal();
  }
};

// AG Grid event handlers
const onGridReady = () => {
  // Grid is ready
};

const handleAction = (action: any, row: any) => {
  switch (action.name) {
    case 'discuss':
      openDiscussionModal(row);
      break;
    case 'edit':
      editNote(row);
      break;
    case 'delete':
      deleteNote(row);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

// Watch for projectId changes
watch(() => props.projectId, () => {
  if (props.projectId) {
    fetchNotes();
  }
});

// Initial load
onMounted(() => {
  if (props.projectId) {
    fetchNotes();
  }
});
</script>

<style scoped>
.project-notes {
  padding: 1rem;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.component-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.header-actions .btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.notes-filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
}

.form-select {
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: white;
}

.loading-indicator, .error-message, .empty-state {
  text-align: center;
  padding: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.grid-content {
  min-height: 400px;
}

/* Priority badge styles */
:deep(.priority-badge) {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  display: inline-block;
  min-width: 60px;
}

:deep(.priority-badge.priority-low) {
  background: var(--success-light);
  color: var(--success-dark);
}

:deep(.priority-badge.priority-normal) {
  background: var(--info-light);
  color: var(--info-dark);
}

:deep(.priority-badge.priority-high) {
  background: var(--warning-light);
  color: var(--warning-dark);
}

:deep(.priority-badge.priority-critical) {
  background: #fef2f2;
  color: #dc2626;
}

/* Action button styles */
:deep(.discuss-btn) {
  background: var(--info-color, #3b82f6);
  color: white;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

:deep(.discuss-btn:hover) {
  background: var(--info-dark, #2563eb);
}

:deep(.edit-btn) {
  background: var(--warning-color, #f59e0b);
  color: white;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

:deep(.edit-btn:hover) {
  background: var(--warning-dark, #d97706);
}

:deep(.delete-btn) {
  background: var(--danger-color, #ef4444);
  color: white;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

:deep(.delete-btn:hover) {
  background: var(--danger-dark, #dc2626);
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background: var(--primary-dark);
}

.btn-secondary {
  background: var(--neutral-100);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover {
  background: var(--neutral-200);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .notes-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .notes-filters {
    flex-direction: column;
  }
  
  .filter-buttons {
    flex-direction: column;
  }
}
</style>
